import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import styled from "styled-components";
import { AnyComponent } from "styled-components/dist/types";
import {
  BookingSummary,
  BookingSummaryProps,
  Button,
  Description,
  Input,
} from "../../../components/index";
import { useState, useEffect } from "react";
import {
  ButtonStyles,
  DescriptionStyles,
  DropdownStyles,
} from "../../../types/styles";
import { useTranslation } from "react-i18next";
import { VenueInfoProps } from "../../../components/VenueInfo/VenueInfo";
import Icon from "../../../components/Icon/Icon";
import {
  CalendarOutline,
  DebitCardReverseOutline,
} from "../../../assets/icons";
import { WidgetThemeStyles } from "../../../domain_objects/CrossSellSetupResponse";
import LoggingService from "../../../services/LoggingService";
import { State } from "../../../types";
import ReturnTableNotice from "../../../components/ReturnTableNotice/ReturnTableNotice";
import { defaultThemeStyles } from "../../../mocks";

const StyledForm = styled.form`
  display: grid;
  row-gap: 8px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 16px 0;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
  z-index: 0;
`;

const StyledInput = styled(Input)`
  height: 44px;
`;

const ExpiryCvcWrapper = styled.div`
  display: flex;
  gap: 8px;
`;

const Label = styled.label<{ styles: DropdownStyles }>`
  font-size: 13px;
  color: ${({ styles }) => styles.DropdownGeneralTextColour};
  font-weight: bold;
`;

const StyledCardElement = styled.div<{
  styles: DropdownStyles;
}>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  height: 44px;
  box-sizing: border-box;
  border: 2px solid ${({ styles }) => styles.DropdownBorderColour};
  border-radius: ${({ styles }) => `${styles.DropdownBorderRadius}px`};
  background-color: ${({ styles }) => styles.DropdownGeneralBackgroundColour};
  &:has(> .StripeElement--focus) {
    outline: 2px solid ${({ styles }) => styles.DropdownBorderFocusColour};
  }

  &.error {
    border-color: ${({ styles }) =>
      styles.DropdownGeneralValidationErrorColour};
    color: ${({ styles }) => styles.DropdownGeneralValidationErrorColour};
  }

  &:has(> .StripeElement--invalid) {
    border-color: ${({ styles }) =>
      styles.DropdownGeneralValidationErrorColour};
    &:has(> .StripeElement--focus) {
      outline: 2px solid
        ${({ styles }) => styles.DropdownGeneralValidationErrorColour};
    }
  }

  .StripeElement {
    flex: 1;
  }
`;

const StyledBookingSummary = styled(BookingSummary)`
  padding: 8px 0;
`;

const PoweredByStripeLogo = styled.img<{
  isDarkBackground?: boolean;
}>`
  content: ${({ isDarkBackground }) =>
    isDarkBackground
      ? "url(https://resdiary.blob.core.windows.net/widgetthemes/powered_by_stripe_white.svg)"
      : "url(https://resdiary.blob.core.windows.net/widgetthemes/Powered%20by%20Stripe%20-%20black.svg)"};
  height: 30px;
  padding-bottom: 12px;
  margin: 0 auto;
`;

export type StripeElementProps = {
  state: State;
  makeBooking: (
    paymentMethodId?: string,
    paymentIntentId?: string,
    confirmationTokenId?: string,
    stripeAuthCallbackFunc?: (
      paymentIntentSecret: string,
      paymentMethodId?: string,
      confirmationTokenId?: string,
    ) => void
  ) => void;
  clientSecret: string | undefined;
  isCreditCardRequired: boolean;
  isPaymentRequired: boolean;
  venueInfo: VenueInfoProps;
  bookingSummary: BookingSummaryProps;
  children?: React.ReactElement<AnyComponent>;
  styles: DropdownStyles & DescriptionStyles & ButtonStyles & WidgetThemeStyles;
  formattedPaymentAmount: string | undefined;
  handleStripeError: () => void;
  isCrossSellChosen?: boolean;
  hostname: string;
  restaurantId?: number;
};

export const StripeElement: React.FC<StripeElementProps> = ({
  state,
  children,
  makeBooking,
  clientSecret,
  isCreditCardRequired,
  isPaymentRequired,
  venueInfo,
  bookingSummary,
  styles,
  isCrossSellChosen,
  formattedPaymentAmount,
  handleStripeError: handleStripeError,
  hostname,
  restaurantId,
}) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [customerName, setCustomerName] = useState<string>();
  const [isDarkBackground, setIsDarkBackground] = useState<boolean>(false);
  const [cardNumberError, setCardNumberError] = useState<string>();
  const [cardCVCError, setCardCVCError] = useState<string>();
  const [cardExpiryError, setCardExpiryError] = useState<string>();
  const [cardValidationFailed, setCardValidationFailed] =
    useState<boolean>(false);
  const [customerNameError, setCustomerNameError] = useState<string>("");

  const loggingService = new LoggingService();

  useEffect(() => {
    if (cardNumberError || cardCVCError || cardExpiryError) {
      setCardValidationFailed(true);
    } else {
      setCardValidationFailed(false);
    }
  }, [cardNumberError, cardCVCError, cardExpiryError]);

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    if (!customerName) {
      setCustomerNameError(t("CardNameRequired"));
    }
    event.preventDefault();
    if (!cardValidationFailed && customerName) {
      if (isCreditCardRequired && !isPaymentRequired) {
        storeCard();
      } else {
        createPayment();
      }
    }
  };

  const createPayment = async () => {
    if (!stripe || !elements) {
      return;
    }
    loggingService.logInfoMessage(
      {
        messageTemplate:
          "ProviderId {providerId} Widget Payment: calling Stripe createPaymentMethod...",
        messageParams: [restaurantId ? restaurantId.toString() : ""],
      },
      hostname
    );
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardNumberElement)!,
      billing_details: {
        name: customerName,
      },
    });
    if (error) {
      if (error.type !== "validation_error") {
        const paymentMethodId = error.payment_method
          ? error.payment_method.id
          : "none";
        loggingService.logErrorMessage(
          {
            messageTemplate: `ProviderId {providerId} Widget Payment: error from Stripe createPaymentMethod. Type - {errorType}. Code - {errorCode}. Decline code - {declineCode}. paymentMethod ID: ${paymentMethodId}`,
            messageParams: [
              restaurantId ? restaurantId.toString() : "",
              error.type,
              error.code ?? "",
              error.decline_code ?? "",
            ],
          },
          hostname
        );
        handleStripeError();
      }
    } else {
      loggingService.logInfoMessage(
        {
          messageTemplate: `ProviderId {providerId} Widget Payment: successfully created paymentMethod ${paymentMethod.id} from Stripe createPaymentMethod.`,
          messageParams: [restaurantId ? restaurantId.toString() : ""],
        },
        hostname
      );
      makeBooking(paymentMethod!.id, undefined, undefined, handleStripeAuth);
    }
  };

  const handleStripeAuth = async (
    paymentIntentSecret: string,
    paymentMethodId?: string,
  ) => {
    if (!stripe) {
      return;
    }
    const { paymentIntent, error } = await stripe.handleCardAction(
      paymentIntentSecret!
    );
    if (error) {
      handleStripeError();
    } else {
      makeBooking(paymentMethodId, paymentIntent?.id);
    }
  };

  const storeCard = async () => {
    if (!stripe || !elements) {
      return;
    }

    if (clientSecret === undefined) {
      console.error("clientSecret is undefined");
      return;
    }
    loggingService.logInfoMessage(
      {
        messageTemplate:
          "ProviderId {providerId} Widget Payment: calling Stripe confirmCardSetup...",
        messageParams: [restaurantId ? restaurantId.toString() : ""],
      },
      hostname
    );
    const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: elements.getElement(CardNumberElement)!,
        billing_details: {
          name: customerName,
        },
      },
    });

    if (error) {
      if (error.type !== "validation_error") {
        const setupIntentId = error.setup_intent
          ? error.setup_intent.id
          : "none";
        loggingService.logErrorMessage(
          {
            messageTemplate:
              `ProviderId {providerId} Widget Payment: error from Stripe confirmCardSetup. Type - {errorType}. Code - {errorCode}. Decline code - {declineCode}. setupIntent ID: ${setupIntentId}` +
              setupIntentId,
            messageParams: [
              restaurantId ? restaurantId.toString() : "",
              error.type,
              error.code ?? "",
              error.decline_code ?? "",
            ],
          },
          hostname
        );
        handleStripeError();
      }
    } else {
      loggingService.logInfoMessage(
        {
          messageTemplate: `ProviderId {providerId} Widget Payment: successfully returned ${setupIntent.payment_method} from Stripe confirmCardSetup`,
          messageParams: [restaurantId ? restaurantId.toString() : ""],
        },
        hostname
      );
      makeBooking(setupIntent.payment_method!.toString());
    }
  };

  useEffect(() => {
    const colour = styles.OverallGeneralBackgroundColour;
    const m = colour.match(/^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i);
    const c_r = parseInt(m![1], 16);
    const c_g = parseInt(m![2], 16);
    const c_b = parseInt(m![3], 16);
    const brightness = (c_r * 299 + c_g * 587 + c_b * 114) / 1000;
    setIsDarkBackground(brightness < 125);
  }, [styles]);

  return (
    <>
      <StyledForm onSubmit={handleSubmit}>
        <Description styles={styles}>
          {isCreditCardRequired
            ? t("TokenizationOnly")
            : `${t("RequiredToPay")} ${formattedPaymentAmount} ${t("ToHoldThisBooking")}`}
        </Description>
        <Container>
          <InputWrapper>
            <StyledInput
              type="text"
              label={t("NameOnCard")}
              onChange={(value) => {
                setCustomerName(value as string);
                if (value) setCustomerNameError("");
              }}
              styles={styles}
              errorMessage={customerNameError}
              dataId="name-on-card"
            />
          </InputWrapper>
          <InputWrapper>
            <Label styles={styles} htmlFor="card-number">
              {t("CardNumber")}
            </Label>
            <StyledCardElement styles={styles}>
              <CardNumberElement
                id="card-number"
                options={{
                  style: {
                    base: {
                      fontSize: "13px",
                      color: styles.DropdownGeneralTextColour,
                      "::placeholder": {
                        color: styles.DropdownGeneralPlaceholderColour,
                      },
                      backgroundColor: "transparent",
                    },
                    invalid: {
                      color: styles.DropdownGeneralValidationErrorColour,
                    },
                  },
                  showIcon: true,
                }}
                onChange={(event) => {
                  event.error
                    ? setCardNumberError(event.error.message)
                    : setCardNumberError("");
                }}
              />
            </StyledCardElement>
          </InputWrapper>
          {cardNumberError && (
            <Description
              styles={{
                OverallGeneralTextColour:
                  styles.DropdownGeneralValidationErrorColour,
              }}
              dataId="card-number-error"
            >
              {cardNumberError}
            </Description>
          )}
          <ExpiryCvcWrapper>
            <InputWrapper>
              <Label styles={styles} htmlFor="card-expiry">
                {t("Expiry")}
              </Label>
              <StyledCardElement styles={styles}>
                <Icon
                  icon={CalendarOutline}
                  color={styles.DropdownGeneralTextColour}
                />
                <CardExpiryElement
                  id="card-expiry"
                  options={{
                    style: {
                      base: {
                        fontSize: "13px",
                        color: styles.DropdownGeneralTextColour,
                        "::placeholder": {
                          color: styles.DropdownGeneralPlaceholderColour,
                        },
                        backgroundColor: "transparent",
                      },
                      invalid: {
                        color: styles.DropdownGeneralValidationErrorColour,
                      },
                    },
                  }}
                  onChange={(event) => {
                    event.error
                      ? setCardExpiryError(event.error.message)
                      : setCardExpiryError("");
                  }}
                />
              </StyledCardElement>
              {cardExpiryError && (
                <Description
                  styles={{
                    OverallGeneralTextColour:
                      styles.DropdownGeneralValidationErrorColour,
                  }}
                  dataId="card-expiry-error"
                >
                  {cardExpiryError}
                </Description>
              )}
            </InputWrapper>
            <InputWrapper>
              <Label styles={styles} htmlFor="card-cvc">
                {t("CVC")}
              </Label>
              <StyledCardElement styles={styles}>
                <Icon
                  icon={DebitCardReverseOutline}
                  color={styles.DropdownGeneralTextColour}
                />
                <CardCvcElement
                  id="card-cvc"
                  options={{
                    style: {
                      base: {
                        fontSize: "13px",
                        color: styles.DropdownGeneralTextColour,
                        "::placeholder": {
                          color: styles.DropdownGeneralPlaceholderColour,
                        },
                        backgroundColor: "transparent",
                      },
                      invalid: {
                        color: styles.DropdownGeneralValidationErrorColour,
                      },
                    },
                  }}
                  onChange={(event) => {
                    event.error
                      ? setCardCVCError(event.error.message)
                      : setCardCVCError("");
                  }}
                />
              </StyledCardElement>
              {cardCVCError && (
                <Description
                  styles={{
                    OverallGeneralTextColour:
                      styles.DropdownGeneralValidationErrorColour,
                  }}
                  dataId="card-cvc-error"
                >
                  {cardCVCError}
                </Description>
              )}
            </InputWrapper>
          </ExpiryCvcWrapper>
        </Container>
        {children}
        <PoweredByStripeLogo isDarkBackground={isDarkBackground} />
        <StyledBookingSummary
          {...bookingSummary}
          {...(isCrossSellChosen && {
            newProvider: {
              location: venueInfo.providerLocation,
              locationUrl: venueInfo.providerLocationUrl,
              name: venueInfo.providerName,
            },
          })}
        />
        {state.bookingDetails.timeSlot?.TimeSlot.IsLeaveTimeRequired &&
          state.bookingDetails.timeSlot?.TimeSlot.LeaveTime && (
            <ReturnTableNotice
              styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
              leaveTime={
                state.promotionDetails?.leaveTime.length
                  ? state.promotionDetails.leaveTime
                  : state.bookingDetails.timeSlot?.TimeSlot.LeaveTime
              }
              isPromotion={!!state.promotionDetails?.leaveTime.length}
            />
          )}
        <Button
          styles={styles}
          onClick={handleSubmit}
          disabled={cardValidationFailed}
          dataId="confirm-booking-button"
        >
          {t("ConfirmBooking")}
        </Button>
      </StyledForm>
    </>
  );
};

export default StripeElement;
